import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useState } from "react";

type ConfirmDialogProps = {
  isOpen: boolean;
  toggle: () => void;
  title: string;
  description: string;
  acceptText: string;
  rejectText: string;
  acceptButtonIsRed?: boolean;
  onSave: (value: boolean) => void;
  requireTextConfirmation?: boolean;
  textConfirmationDescription?: string;
  textConfirmationValue?: string;
};

const ConfirmDialog = (props: ConfirmDialogProps) => {
  const [textConfirmationValue, setTextConfirmationValue] = useState("");
  return (
    <Transition appear show={props.isOpen} as={Fragment}>
      <Dialog
        open={props.isOpen}
        onClose={() => {
          setTextConfirmationValue("");
          props.toggle();
        }}
        className="relative z-[150]"
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="mx-auto w-96 h-96 max-w-sm rounded bg-white p-8">
              <Dialog.Title className="text-primary-blue text-2xl uppercase text-center">
                {props.title}
              </Dialog.Title>
              <p className="text-center my-8">{props.description}</p>
              {props.requireTextConfirmation && (
                <div className="flex justify-center my-4">
                  <p className="text-center my-8">
                    {props.textConfirmationDescription}
                  </p>
                  <input
                    type="text"
                    className="w-full inline-flex tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                    value={textConfirmationValue}
                    onChange={(e) => setTextConfirmationValue(e.target.value)}
                    placeholder={props.textConfirmationValue}
                  />
                </div>
              )}
              <div className="flex justify-center my-4">
                <button
                  disabled={
                    props.requireTextConfirmation &&
                    textConfirmationValue !== props.textConfirmationValue
                  }
                  onClick={() => {
                    setTextConfirmationValue("");
                    props.onSave(true);
                  }}
                  className={`w-full inline-flex uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-white border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-primary-blue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 ${
                    props.acceptButtonIsRed ? "bg-red-800 border-red-800" : ""
                  }`}
                >
                  {props.acceptText}
                </button>
              </div>
              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setTextConfirmationValue("");
                    props.onSave(false);
                  }}
                  className="w-full inline-flex uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                >
                  {props.rejectText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ConfirmDialog;
